/**
 * Point-in-polygon test using ray casting algorithm
 * Checks if a point is inside a polygon
 * 
 * @param point - [longitude, latitude] coordinates
 * @param polygon - Array of [longitude, latitude] coordinates forming a polygon
 * @returns true if point is inside polygon
 */
export function pointInPolygon(
  point: [number, number],
  polygon: [number, number][]
): boolean {
  const [x, y] = point;
  let inside = false;

  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [xi, yi] = polygon[i];
    const [xj, yj] = polygon[j];

    const intersect =
      yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersect) {
      inside = !inside;
    }
  }

  return inside;
}

/**
 * Check if a point is inside any polygon in a GeoJSON feature
 * Handles Point, Polygon, and MultiPolygon geometries
 * 
 * @param point - [longitude, latitude] coordinates
 * @param feature - GeoJSON feature
 * @returns true if point is inside the feature
 */
export function pointInGeoJSONFeature(
  point: [number, number],
  feature: GeoJSON.Feature
): boolean {
  const geometry = feature.geometry;

  if (geometry.type === "Point") {
    const [lon, lat] = geometry.coordinates;
    return point[0] === lon && point[1] === lat;
  }

  if (geometry.type === "Polygon") {
    // First ring is exterior, others are holes
    const exteriorRing = geometry.coordinates[0] as [number, number][];
    const isInExterior = pointInPolygon(point, exteriorRing);
    
    if (!isInExterior) {
      return false;
    }

    // Check if point is in any hole (if so, it's outside)
    for (let i = 1; i < geometry.coordinates.length; i++) {
      if (pointInPolygon(point, geometry.coordinates[i] as [number, number][])) {
        return false;
      }
    }

    return true;
  }

  if (geometry.type === "MultiPolygon") {
    // Check if point is in any polygon
    for (const polygon of geometry.coordinates) {
      const exteriorRing = polygon[0] as [number, number][];
      const isInExterior = pointInPolygon(point, exteriorRing);
      
      if (isInExterior) {
        // Check if point is in any hole
        let isInHole = false;
        for (let i = 1; i < polygon.length; i++) {
          if (pointInPolygon(point, polygon[i] as [number, number][])) {
            isInHole = true;
            break;
          }
        }
        
        if (!isInHole) {
          return true;
        }
      }
    }

    return false;
  }

  return false;
}

/**
 * Calculate the centroid of a polygon
 * 
 * @param polygon - Array of [longitude, latitude] coordinates
 * @returns [longitude, latitude] of the centroid
 */
export function polygonCentroid(polygon: [number, number][]): [number, number] {
  let x = 0;
  let y = 0;
  const len = polygon.length;

  for (const [lon, lat] of polygon) {
    x += lon;
    y += lat;
  }

  return [x / len, y / len];
}

/**
 * Calculate the centroid of a GeoJSON feature
 * 
 * @param feature - GeoJSON feature
 * @returns [longitude, latitude] of the centroid, or null if unable to calculate
 */
export function featureCentroid(feature: GeoJSON.Feature): [number, number] | null {
  const geometry = feature.geometry;

  if (geometry.type === "Point") {
    return geometry.coordinates as [number, number];
  }

  if (geometry.type === "Polygon") {
    // Use exterior ring for centroid
    const exteriorRing = geometry.coordinates[0] as [number, number][];
    return polygonCentroid(exteriorRing);
  }

  if (geometry.type === "MultiPolygon") {
    // Calculate centroid of first polygon (could be improved to use weighted average)
    if (geometry.coordinates.length > 0 && geometry.coordinates[0].length > 0) {
      const exteriorRing = geometry.coordinates[0][0] as [number, number][];
      return polygonCentroid(exteriorRing);
    }
  }

  return null;
}

/**
 * Calculate bounding box for a GeoJSON feature
 * Returns [minLon, minLat, maxLon, maxLat]
 */
export function featureBoundingBox(feature: GeoJSON.Feature): [number, number, number, number] | null {
  const geometry = feature.geometry;

  if (geometry.type === "Point") {
    const [lon, lat] = geometry.coordinates as [number, number];
    return [lon, lat, lon, lat];
  }

  let minLon = Infinity;
  let minLat = Infinity;
  let maxLon = -Infinity;
  let maxLat = -Infinity;

  const processCoordinates = (coords: any[]) => {
    if (Array.isArray(coords[0]) && typeof coords[0][0] === "number") {
      // Array of [lon, lat] pairs
      coords.forEach((coord: any) => {
        if (Array.isArray(coord[0])) {
          processCoordinates(coord);
        } else {
          const [lon, lat] = coord as [number, number];
          minLon = Math.min(minLon, lon);
          minLat = Math.min(minLat, lat);
          maxLon = Math.max(maxLon, lon);
          maxLat = Math.max(maxLat, lat);
        }
      });
    }
  };

  if (geometry.type === "Polygon") {
    geometry.coordinates.forEach((ring: any) => {
      processCoordinates(ring);
    });
  } else if (geometry.type === "MultiPolygon") {
    geometry.coordinates.forEach((polygon: any) => {
      polygon.forEach((ring: any) => {
        processCoordinates(ring);
      });
    });
  }

  if (minLon === Infinity) {
    return null;
  }

  return [minLon, minLat, maxLon, maxLat];
}

/**
 * Check if a point is within a bounding box
 */
export function pointInBoundingBox(
  point: [number, number],
  bbox: [number, number, number, number]
): boolean {
  const [lon, lat] = point;
  const [minLon, minLat, maxLon, maxLat] = bbox;
  return lon >= minLon && lon <= maxLon && lat >= minLat && lat <= maxLat;
}

